import { useAccountability } from 'hooks/accountability';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { Permissions, userHasPermission, userHasRole } from 'services/Authorization';
import strings from 'strings';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { accountablePermissionsManagement } from 'UI/utils/inventory';

const COPIES = strings.inventoryProfiles.common;

/**
 * @typedef {Object} RecruiterView
 * @property {'owner' | 'accountable'} key
 * @property {Object} recruiter
 * @property {Object} coach
 * @property {Object} assistant
 * @property {boolean} isReassignButtonVisible
 */

/**
 * @typedef {Object} FreeGameAction
 * @property {boolean} isVisible
 * @property {string} label
 */

/**
 * @typedef {Object} ProfileAccountability
 * @property {boolean} isVisible
 * @property {RecruiterView[]} recruiterViews Owner first, accountable second (only the ones that exist)
 * @property {FreeGameAction} freeGameAction
 * @property {() => void} onReassignClick
 * @property {boolean} canManage
 * @property {boolean} canView
 * @property {boolean} canCollaborate
 * @property {boolean} hasAssistant
 */

/**
 * What the subheader and the ⋮ menu need to know about the team of a profile. It repeats the
 * rules of V1's InventoryProfileLayout once, for the five profiles: AssignableEntities and
 * TeamworkEntities (hooks/accountability.js) are read through useAccountability.
 * @param {Object} params
 * @param {Object} params.entity ENTITIES.<entity> (`id` and `requestAssignmentLabel`)
 * @param {Object} params.profile Profile from the domain hook (an empty object while loading)
 * @param {boolean} params.canEdit `canEdit` of the entity permissions, so the Recruiters label and
 *   the collaborator item follow the same edit rule as the Edit item (V1 InventoryProfileLayout)
 * @param {() => void} params.onReassign Opens the recruiters drawer; pass `openDrawer(Drawers.Recruiters)`.
 *   Containers that have no reassign button (Contact) may omit it.
 * @returns {ProfileAccountability}
 */
export const useProfileAccountability = ({ canEdit, entity, profile, onReassign = () => {} }) => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const accountability = useAccountability(user, profile, entity);
  const {
    accountableRecruiter,
    assistantForAccountableRecruiter,
    assistantRecruiter,
    isAccountableCoach,
    isAccountableRecruiter,
    isAssignableEntity,
    isAssistantRecruiter,
    isItemMine,
    isMainCoach,
    isMainRecruiter,
    isOfficeAssigned,
    isTeamworkEntity
  } = accountability;

  const isLoaded = Boolean(profile?.id);
  const isUserCoach = userHasRole(Roles.Coach);
  const isUserRecruiter = userHasRole(Roles.Recruiter);
  const canUseFreeGameButtons = checkIfFeatureFlagEnabled(FeatureFlags.FreeGameButtons);

  const { isAssistantButtonEnabled, isAssistantForAccountableButtonEnabled } =
    accountablePermissionsManagement({
      assistantForAccountableRecruiter,
      assistantRecruiter,
      isAccountableCoach,
      isAccountableRecruiter,
      isMainCoach,
      isMainRecruiter,
      isTeamworkEntity
    });

  const showRecruiterAndAccountable =
    (isTeamworkEntity && profile?.free_game && !isAssistantRecruiter) ||
    Boolean(accountableRecruiter);

  const ownerView =
    isLoaded && profile.recruiter
      ? {
          key: 'owner',
          recruiter: profile.recruiter,
          coach: profile.coach,
          assistant: assistantRecruiter,
          isReassignButtonVisible: isAssistantButtonEnabled
        }
      : null;

  const accountableView =
    isLoaded && accountableRecruiter
      ? {
          key: 'accountable',
          recruiter: accountableRecruiter.recruiter,
          coach: accountableRecruiter.coach,
          assistant: assistantForAccountableRecruiter,
          isReassignButtonVisible: isAssistantForAccountableButtonEnabled
        }
      : null;

  const recruiterViews = [ownerView, accountableView].filter(Boolean);

  const freeGameAction = {
    isVisible:
      isLoaded &&
      showRecruiterAndAccountable &&
      !accountableRecruiter &&
      canUseFreeGameButtons &&
      !isItemMine &&
      !isOfficeAssigned,
    label: isUserCoach ? COPIES.assignRecruiter : entity.requestAssignmentLabel
  };

  return {
    canCollaborate: isUserRecruiter && canEdit && isTeamworkEntity,
    canManage:
      isAssignableEntity &&
      ((isUserCoach && canEdit) || userHasPermission(Permissions.Inventory.OverrideAssignment)),
    canView: isAssignableEntity,
    freeGameAction,
    hasAssistant: Boolean(assistantRecruiter?.recruiter),
    isVisible: recruiterViews.length > 0 || freeGameAction.isVisible,
    onReassignClick: onReassign,
    recruiterViews
  };
};
